import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {
    DefaultButton,
    DetailsListLayoutMode,
    IColumn,
    IconButton,
    Modal,
    SelectionMode,
    Separator,
    ShimmeredDetailsList,
    Stack,
    Text
} from "@fluentui/react";
import {contentStyles, iconButtonStyles} from "./index";
import {useBranding} from "../../hooks/useBranding";
import StatusMessage, {StatusEnum} from "../../share/StatusMessage";
import {IDocument} from "../../App";
import ApprovalService, {
    IGetApprovalStepActionStateRequest,
    IGetApprovalStepActionStateResponse
} from "../../services/Approval";
import {formatFromUTC, formatSimpleDate} from "../../Helpers/DateUtils";
import Helper from "../../Helpers/Helper";


type Properties = {
    showModal: boolean,
    document: IDocument | undefined
    onDismissModal: () => void
}

export function ApprovalStepActionState({
                                            showModal,
                                            onDismissModal,
                                            document,
                                        }: Properties) {

    const {t,} = useTranslation(['approval', 'common'])
    const {branding} = useBranding()

    const [messageApprove, setMessageApprove] = useState("")
    const [showMessageApprove, setShowMessageApprove] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [statusMessageApprove, setStatusMessageApprove] = useState<StatusEnum>(StatusEnum.Info)
    const [hasTimerMessageApprove, setHasTimerMessageApprove] = useState(false)
    const [items, setItems] = useState<IGetApprovalStepActionStateResponse[]>([])


    useEffect(() => {
        if (showModal) {
            init().then()
        }
    }, [showModal])


    const init = async () => {

        setIsLoading(true)
        setItems([])

        const request: IGetApprovalStepActionStateRequest = {
            Id: String(document?.Id ?? '0'),
            Mode: "documentid",
            TemplateId: String(document?.TemplateId ?? '0')
        }


        await ApprovalService.getApprovalStepActionState(request)
            .then(data => {
                try {
                    const resultData = JSON.parse(data.Result) as Array<IGetApprovalStepActionStateResponse>

                    if (Array.isArray(resultData)) {
                        resultData.sort((a, b) => a.Id - b.Id);
                        setItems(resultData)
                    } else {
                        ShowMessageApprove(t('message.notFound', {ns: 'common'}), StatusEnum.Error)
                    }
                } catch (error) {
                    ShowMessageApprove(t('message.error', {ns: 'common', Error: error.toString()}), StatusEnum.Error)
                }
            })
            .catch(error => {
                ShowMessageApprove(t('message.error', {ns: 'common', Error: error}), StatusEnum.Error)
            })
            .finally(() => {
                setIsLoading(false)
            })

    }

    /**
     * Format date
     * @param date
     * @param format
     * @param utc
     */
    const onFormatDate = (date?: Date, format: string = "ll", utc: boolean = false): string => {
        const _locale = !!sessionStorage.getItem("language")
            ? sessionStorage.getItem("language")!.toString()
            : "en";
        if (utc) return date !== null ? formatFromUTC(date, format, _locale) : null
        else return date !== null ? formatSimpleDate(date, format, _locale) : null
    }


    /**
     * Displays an approval message with a specified status.
     *
     * @param {string} message - The message content to be displayed.
     * @param {StatusEnum} status - The status associated with the approval message.
     * @param hasTimer
     * @return {void} This function does not return a value.
     */

    function ShowMessageApprove(message: string, status: StatusEnum, hasTimer: boolean = false): void {
        setMessageApprove(message)
        setStatusMessageApprove(status)
        setHasTimerMessageApprove(hasTimer)
        setShowMessageApprove(true)
    }


    const approvalNotesColumns: IColumn[] = [
        {
            key: 'column1',
            name: t('title.colStepName', {ns: 'approval'}),
            fieldName: 'ApprovalStepName',
            minWidth: 120,
            maxWidth: 120,
            isMultiline: false,
            isResizable: true,
            onRender: (item: IGetApprovalStepActionStateResponse) => {
                return <>
                    <Stack styles={{
                        root: {
                            justifyContent: "center",
                            alignItems: "start",
                            height: "100%",
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                        }
                    }}>
                        <Text variant={"medium"}
                              title={item.ApprovalStepName}
                        >
                            {item.ApprovalStepName}
                        </Text>
                    </Stack>
                </>
            }
        },
        {
            key: 'column2',
            name: t('title.colEmailAddress', {ns: 'approval'}),
            fieldName: 'EmailAddress',
            minWidth: 140,
            isMultiline: false,
            isResizable: true,
            onRender: (item: IGetApprovalStepActionStateResponse) => {
                return <>
                    <Stack styles={{
                        root: {
                            justifyContent: "center",
                            alignItems: "start",
                            height: "100%"
                        }
                    }}>
                        <Text variant={"medium"}
                              styles={{
                                  root: {
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      width: '100%',
                                  }
                              }}
                              block
                              title={item.EmailAddress}
                        >
                            {item.EmailAddress}
                        </Text>
                    </Stack>
                </>
            }
        },
        {
            key: 'column3',
            name: t('title.colResponseAction', {ns: 'approval'}),
            fieldName: 'ApprovalStepActionNameResponse',
            minWidth: 120,
            maxWidth: 120,
            isMultiline: false,
            isResizable: true,
            onRender: (item: IGetApprovalStepActionStateResponse) => {
                return <>
                    <Stack styles={{
                        root: {
                            justifyContent: "center",
                            alignItems: "start",
                            height: "100%"
                        }
                    }}>
                        <Text variant={"medium"}
                              styles={{
                                  root: {
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      width: '100%',

                                  }
                              }}
                              block
                              title={item.ApprovalStepActionNameResponse}
                        >
                            {item.ApprovalStepActionNameResponse}
                        </Text>
                    </Stack>
                </>
            }
        },
        {
            key: 'column4',
            name: t('title.colEvent', {ns: 'approval'}),
            fieldName: 'OnEvent',
            minWidth: 120,
            maxWidth: 120,
            isMultiline: false,
            isResizable: true,
            onRender: (item: IGetApprovalStepActionStateResponse) => {
                return <>
                    <Stack styles={{
                        root: {
                            justifyContent: "center",
                            alignItems: "start",
                            height: "100%"
                        }
                    }}>
                        <Text variant={"medium"}
                              styles={{
                                  root: {
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      width: '100%'
                                  }
                              }}
                              block
                              title={item.DoAction}
                        >
                            {item.OnEvent !== null && item.OnEvent !== undefined && item.OnEvent.startsWith('On')
                                ? item.OnEvent.slice(2)
                                : item.OnEvent}
                        </Text>
                    </Stack>
                </>
            }
        },
        {
            key: 'column5',
            name: t('title.colDateApprovalSent', {ns: 'approval'}),
            fieldName: 'DateApprovalSent',
            minWidth: 160,
            maxWidth: 160,
            isMultiline: false,
            isResizable: true,
            onRender: (item: IGetApprovalStepActionStateResponse) => {
                return <>
                    <Stack styles={{
                        root: {
                            justifyContent: "center",
                            alignItems: "start",
                            height: "100%"
                        }
                    }}>

                        {item.DateApprovalSent === null ?
                            ' '
                            :
                            <Text block variant={"medium"}
                                  styles={{
                                      root: {
                                          whiteSpace: "nowrap",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          width: '100%',

                                      }
                                  }}
                                  title={onFormatDate(new Date(item.DateApprovalSent), "lll")}>
                                {onFormatDate(new Date(item.DateApprovalSent), "ll", true)}
                            </Text>
                        }
                    </Stack>
                </>
            }
        },
        {
            key: 'column6',
            name: t('title.colDateApprovalReceived', {ns: 'approval'}),
            fieldName: 'DateResponseReceived',
            minWidth: 160,
            maxWidth: 160,
            isMultiline: false,
            isResizable: true,
            onRender: (item: IGetApprovalStepActionStateResponse) => {
                return <>
                    <Stack styles={{
                        root: {
                            justifyContent: "center",
                            alignItems: "start",
                            height: "100%"
                        }
                    }}>
                        {item.DateResponseReceived === null ?
                            ' '
                            :
                            <Text block variant={"medium"}

                                  styles={{
                                      root: {
                                          whiteSpace: "nowrap",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          width: '100%'
                                      }
                                  }}

                                  title={onFormatDate(new Date(item.DateResponseReceived), "lll")}>
                                {onFormatDate(new Date(item.DateResponseReceived), "ll", true)}
                            </Text>
                        }
                    </Stack>
                </>
            }
        }

    ]


    return (
        <>
            <Modal
                isOpen={showModal}
                isBlocking={true}
                containerClassName={contentStyles(branding.theme).containerApprovalNote}
            >

                <Stack grow={1} styles={{root: {height: "100%"}}}>
                    <Stack tokens={{childrenGap: 8}} styles={{
                        root: {
                            height: 56,
                            alignItems: 'center'
                        }
                    }} horizontal>

                        <Stack grow={1}
                               styles={{
                                   root: {
                                       textOverflow: "ellipsis",
                                       paddingLeft: 16,
                                       whiteSpace: "nowrap",
                                       overflow: "hidden"
                                   }
                               }}>
                            <Text variant={"xLarge"}
                                  title={t('title.approvalState', {ns: 'approval', Name: Helper.AddParentheses(document?.Description)})}>
                                {t('title.approvalState', {ns: 'approval', Name: Helper.AddParentheses(document?.Description)})}
                            </Text>
                        </Stack>

                        <Stack horizontal styles={{root: {alignItems: "center", paddingRight: 8}}}>
                            <IconButton
                                styles={iconButtonStyles}
                                iconProps={{iconName: 'Cancel'}}
                                ariaLabel={t('Common.Button.Close', {ns: 'common'})}
                                onClick={onDismissModal}
                                title={t('Common.Button.Close', {ns: 'common'})}
                            />
                        </Stack>
                    </Stack>
                    <Stack grow={1} styles={{root: {height: 1}}}>

                        <Stack horizontal styles={{root: {height: '100%'}}}>

                            <Stack grow={1} styles={{root: {height: '100%', width: 1}}}>

                                {/*-- START CONTENT --*/}
                                <Stack grow={1} styles={{root: {overflow: 'auto', paddingLeft: 16, paddingRight: 16}}}
                                       className="scrollVisible">

                                    {showMessageApprove &&
                                        <>
                                            <Stack styles={{root: {paddingTop: 8}}}>
                                                <StatusMessage status={statusMessageApprove}
                                                               hasTimer={hasTimerMessageApprove}
                                                               truncated={true}
                                                               isMultiline={true}
                                                               dismiss={true}

                                                               setShowMessage={setShowMessageApprove}>
                                                    <div>{messageApprove}</div>
                                                </StatusMessage>
                                            </Stack>
                                        </>
                                    }

                                    <Stack styles={{root: {width: 1024}}}>
                                        <ShimmeredDetailsList
                                            items={items}
                                            enableShimmer={isLoading}
                                            compact={false}
                                            columns={approvalNotesColumns}
                                            selectionMode={SelectionMode.none}
                                            layoutMode={DetailsListLayoutMode.fixedColumns}
                                            isHeaderVisible={true}
                                            isSelectedOnFocus={false}
                                            setKey={"Id"}
                                        />
                                    </Stack>

                                </Stack>
                                {/*-- END CONTENT --*/}

                                {/*-- START FOOTER --*/}
                                <Separator></Separator>
                                <Stack horizontal tokens={{childrenGap: 8}}
                                       styles={{root: {paddingRight: 16, paddingBottom: 12, alignItems: 'center'}}}>
                                    <Stack grow={1}>&nbsp;</Stack>

                                    <DefaultButton
                                        onClick={onDismissModal}
                                        styles={{root: {marginRight: 8}}}
                                        text={t('Common.Button.Close', {ns: 'common'})}
                                        title={t('Common.Button.Close', {ns: 'common'})}/>

                                </Stack>
                                {/*-- END FOOTER --*/}

                            </Stack>

                        </Stack>
                    </Stack>
                </Stack>

            </Modal>
        </>
    )
}


